import QueryKeys from '@/utils/QueryKeys';
import { ChildrenMap } from '@/components/shared/Standards/standardsTreeUtils';

export type ParentMap = Map<number, number | null>;
export type CheckboxState = 'checked' | 'indeterminate' | 'unchecked';

// AIDEV-NOTE: checkedIds holds explicit selections only, never auto-filled descendants. A node is
// "checked" when it or any ancestor is in the set, so a checked parent covers children that load
// later, and the set itself is the minimal selection the results filter (PLAT-17642) will send.

const ancestorsOf = (id: number, parentMap: ParentMap): number[] => {
  const path: number[] = [];
  let current = parentMap.get(id);

  while (current != null) {
    path.push(current);
    current = parentMap.get(current);
  }

  return path;
};

export const isEffectivelyChecked = (id: number, checkedIds: Set<number>, parentMap: ParentMap) => (
  checkedIds.has(id) || ancestorsOf(id, parentMap).some(ancestor => checkedIds.has(ancestor))
);

export const hasCheckedDescendant = (id: number, checkedIds: Set<number>, childrenMap: ChildrenMap): boolean => (
  (childrenMap.get(id) ?? []).some(child => (
    checkedIds.has(child.id) || hasCheckedDescendant(child.id, checkedIds, childrenMap)
  ))
);

export const getCheckboxState = (
  id: number,
  checkedIds: Set<number>,
  parentMap: ParentMap,
  childrenMap: ChildrenMap,
): CheckboxState => {
  if (isEffectivelyChecked(id, checkedIds, parentMap)) return 'checked';
  if (hasCheckedDescendant(id, checkedIds, childrenMap)) return 'indeterminate';
  return 'unchecked';
};

const removeDescendants = (id: number, set: Set<number>, childrenMap: ChildrenMap) => {
  (childrenMap.get(id) ?? []).forEach((child) => {
    set.delete(child.id);
    removeDescendants(child.id, set, childrenMap);
  });
};

// Unchecking a node beneath a checked ancestor: the ancestor's selection is replaced by explicit
// checks on every loaded node that hangs off the ancestor→node path, except the path itself.
// Lossless only when every path level is fully loaded — the caller must first complete any
// partially fetched ("See more") child lists along the path (see ensureChildrenFullyLoaded).
const explodeAncestor = (
  ancestor: number,
  target: number,
  set: Set<number>,
  parentMap: ParentMap,
  childrenMap: ChildrenMap,
) => {
  const path = new Set(ancestorsOf(target, parentMap));
  set.delete(ancestor);

  const visit = (id: number) => {
    (childrenMap.get(id) ?? []).forEach((child) => {
      if (child.id === target) return; // the unchecked node: leave it and its subtree deselected

      if (path.has(child.id)) {
        visit(child.id);
      } else {
        set.add(child.id);
      }
    });
  };

  visit(ancestor);
};

export const nearestCheckedAncestor = (id: number, checkedIds: Set<number>, parentMap: ParentMap) => (
  ancestorsOf(id, parentMap).find(ancestor => checkedIds.has(ancestor)) ?? null
);

// The nodes whose children the explode enumerates: the checked ancestor down to the target's
// parent. Their child lists must be fully loaded before exploding, or unfetched "See more"
// siblings would silently fall out of the selection.
export const explodedLevelParentIds = (id: number, ancestor: number, parentMap: ParentMap): number[] => (
  [ancestor, ...ancestorsOf(id, parentMap).filter((node) => {
    const above = new Set(ancestorsOf(node, parentMap));
    return above.has(ancestor);
  })]
);

export const applyCheck = (
  checkedIds: Set<number>,
  id: number,
  checked: boolean,
  parentMap: ParentMap,
  childrenMap: ChildrenMap,
): Set<number> => {
  const next = new Set(checkedIds);

  if (checked) {
    next.add(id);
    removeDescendants(id, next, childrenMap);
    return next;
  }

  if (next.has(id)) {
    next.delete(id);
    return next;
  }

  const checkedAncestor = nearestCheckedAncestor(id, next, parentMap);
  if (checkedAncestor != null) explodeAncestor(checkedAncestor, id, next, parentMap, childrenMap);

  return next;
};

export const standardsQueryKey = (stateId: number, programId: number) => (
  [QueryKeys.Standards, 'correlated', stateId, programId] as const
);
